import type {
  CampaignCreationCapabilities,
  SmartCreationDraft,
  SmartCreationProductOrganization,
} from "@adpilot/shared";
import { IconCircleCheck, IconHierarchy, IconTargetArrow } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { ProductListingPicker } from "@/components/campaign-creation/ProductListingPicker";
import { ShopScopeSelect } from "@/components/layout/shop-scope-select";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useIsVendorShop } from "@/hooks/use-shop-account-type";
import type { CampaignCreationInvalidProduct } from "@/lib/campaign-creation-session";
import { cn } from "@/lib/utils";

export interface ProductsStepProps {
  draft: SmartCreationDraft;
  shopId: string;
  shopChanging: boolean;
  pendingShopId: string | null;
  shopTransitionMinimumElapsed: boolean;
  onShopChange: (shopId: string) => Promise<void>;
  capabilities: CampaignCreationCapabilities;
  enterInput: string;
  invalidProducts: CampaignCreationInvalidProduct[];
  onInvalidProductsChange: (products: CampaignCreationInvalidProduct[]) => void;
  onCatalogBlockingChange: (blocked: boolean) => void;
  onDraftChange: (draft: SmartCreationDraft, preserveRecommendation?: boolean) => void;
  onEnterInputChange: (value: string) => void;
  onReconnect: () => void;
}

/** Products step: creation Shop, product selection, and how products map to campaigns. */
export function ProductsStep({
  draft,
  shopId,
  shopChanging,
  pendingShopId,
  shopTransitionMinimumElapsed,
  onShopChange,
  capabilities,
  enterInput,
  invalidProducts,
  onInvalidProductsChange,
  onCatalogBlockingChange,
  onDraftChange,
  onEnterInputChange,
  onReconnect,
}: ProductsStepProps) {
  const { t } = useTranslation("smartCreation");
  // Smart Creation products carry their listing key as `sku`: a seller's SKU,
  // or a vendor's ASIN, which has no SKU of its own (#674).
  const vendor = useIsVendorShop(shopId);
  return (
    <div className="flex w-full flex-col gap-4" data-testid="smart-creation-products-step">
      <div className="flex justify-end">
        <div className="flex shrink-0 items-center gap-2">
          {shopChanging ? (
            <span role="status" className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Spinner className="size-3.5" />
              {t("products.shopSelection.changing")}
            </span>
          ) : null}
          <ShopScopeSelect
            selectionMode="single"
            value={pendingShopId ?? shopId}
            onValueChange={(nextShopId) => void onShopChange(nextShopId)}
            disabled={shopChanging}
            ariaLabel={t("products.shopSelection.label")}
            className="w-72 max-w-full"
          />
        </div>
      </div>
      <ProductListingPicker
        shopId={shopId}
        marketplaceId={capabilities.marketplaceId}
        selected={draft.products.map((product) => ({
          ...product,
          listingKey: product.sku,
          sku: vendor ? null : product.sku,
        }))}
        onSelectedChange={(products) =>
          onDraftChange({
            ...draft,
            products: products.map(({ listingKey, ...product }) => ({
              ...product,
              sku: listingKey,
              eligibility: product.eligibility ?? "unknown",
            })),
            targetAcos: null,
            totalDailyBudget: null,
          })
        }
        enterInput={enterInput}
        onEnterInputChange={onEnterInputChange}
        invalidProducts={invalidProducts}
        onInvalidProductsChange={onInvalidProductsChange}
        onCatalogBlockingChange={onCatalogBlockingChange}
        onReconnect={onReconnect}
        selectionLimit={500}
        initialAvailability={null}
        showAvailabilityFilter={false}
        allowCatalogRefresh={false}
        showInventoryRefresh
        blockProductList={shopChanging}
        refreshInventoryOnMount={false}
        showBlockedProductError={shopTransitionMinimumElapsed}
        adProduct="sp"
        unifiedTable
        deferEligibilityUntilContinue
        onProductsAdded={() => undefined}
      />
      <ProductOrganizationStep
        organization={draft.productOrganization}
        onOrganizationChange={(productOrganization) =>
          onDraftChange({
            ...draft,
            productOrganization,
            targetAcos: null,
            totalDailyBudget: null,
          })
        }
      />
    </div>
  );
}

function ProductOrganizationStep({
  organization,
  onOrganizationChange,
}: {
  organization: SmartCreationProductOrganization;
  onOrganizationChange: (organization: SmartCreationProductOrganization) => void;
}) {
  const { t } = useTranslation("smartCreation");
  const choices: Array<{
    value: SmartCreationProductOrganization;
    title: string;
    description: string;
    guidance: string;
    icon: React.ReactNode;
  }> = [
    {
      value: "campaign",
      title: t("products.organization.campaignTitle"),
      description: t("products.organization.campaignDescription"),
      guidance: t("products.organization.campaignGuidance"),
      icon: <IconHierarchy />,
    },
    {
      value: "ad_group",
      title: t("products.organization.adGroupTitle"),
      description: t("products.organization.adGroupDescription"),
      guidance: t("products.organization.adGroupGuidance"),
      icon: <IconTargetArrow />,
    },
  ];

  return (
    <section className="flex flex-col gap-4" aria-labelledby="smart-creation-organization-title">
      <div className="flex items-end justify-between gap-6">
        <div className="flex flex-col gap-1">
          <h3 id="smart-creation-organization-title" className="text-base font-semibold text-ink">
            {t("products.organization.title")}
          </h3>
          <p className="text-xs leading-5 text-muted-foreground">
            {t("products.organization.description")}
          </p>
        </div>
      </div>

      <ToggleGroup
        value={[organization]}
        onValueChange={(values) => {
          const value = values[0];
          if (value === "campaign" || value === "ad_group") onOrganizationChange(value);
        }}
        aria-label={t("products.organization.title")}
        className="grid w-full grid-cols-2 items-stretch gap-3"
      >
        {choices.map((choice) => {
          const selected = organization === choice.value;
          return (
            <ToggleGroupItem
              key={choice.value}
              value={choice.value}
              aria-label={choice.title}
              className={cn(
                "h-auto min-h-32 w-full min-w-0 items-start justify-start gap-3 rounded-md border bg-surface p-4 text-left whitespace-normal transition-colors hover:border-ink/40 hover:bg-subtle/35 aria-pressed:bg-surface aria-pressed:hover:bg-surface",
                selected ? "border-ink" : "border-line",
              )}
            >
              <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-subtle [&>svg]:size-5">
                {choice.icon}
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-1.5">
                <span className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-ink">{choice.title}</span>
                  {choice.value === "campaign" ? (
                    <Badge variant={selected ? "inverse" : "neutral"}>
                      {t("products.organization.default")}
                    </Badge>
                  ) : null}
                </span>
                <span className="text-xs font-normal leading-[18px] text-muted-foreground">
                  {choice.description}
                </span>
                <span className="mt-1 text-xs font-normal leading-[18px] text-ink/80">
                  {choice.guidance}
                </span>
              </span>
              {selected ? <IconCircleCheck className="size-5 shrink-0" /> : null}
            </ToggleGroupItem>
          );
        })}
      </ToggleGroup>
    </section>
  );
}
